import * as Yup from "yup";
import { buildPhoneNumberSchema } from "../shared/phoneValidation";
import { paymentTermsOptions } from "../../../utils/Dropdowns";

export const DRAFT_KEY = "threepl_load_booking_draft";

export const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "In Transit", value: "in_transit" },
  { label: "Delayed", value: "delayed" },
  { label: "Completed", value: "completed" },
];

export const statusFilterOptions = statusOptions;

export const vehicleTypeOptions = [
  { label: "4-Wheeler Mazda", value: "4_wheeler_mazda" },
  { label: "10-Wheeler Truck", value: "10_wheeler_truck" },
  { label: "Trailer", value: "trailer" },
];

export const goodsTypeOptions = [
  { label: "FMCGs", value: "fmcgs" },
  { label: "Medicine", value: "medicine" },
  { label: "Spare Parts", value: "spare_parts" },
  { label: "Hardware", value: "hardware" },
];

export const handlingUnitOptions = [
  { label: "Box", value: "box" },
  { label: "Crate", value: "crate" },
  { label: "Pallet", value: "pallet" },
  { label: "Container", value: "container" },
];

export const unitOptions = [
  { label: "kg", value: "kg" },
  { label: "ton", value: "ton" },
  { label: "pieces", value: "pieces" },
];

export const paymentModeOptions = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
];

export const paymentTermOptions = paymentTermsOptions;

export const billingFrequencyOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
];

export const fallbackBankOptions = [
  { label: "AL-HABIB BANK", value: "al_habib_bank" },
  { label: "HBL", value: "hbl" },
  { label: "UBL", value: "ubl" },
];

export const insuranceCompanyOptions = [
  { label: "TPL Insurance", value: "tpl_insurance" },
  { label: "EFU Insurance", value: "efu_insurance" },
  { label: "Adamjee Insurance", value: "adamjee_insurance" },
];

export const locationOptions = [
  { label: "Port Qasim Bin Qasim Road, Karachi", value: "port_qasim_bin_qasim" },
  { label: "Karachi Port, Karachi", value: "karachi_port_karachi" },
  { label: "Port of Pasni, Pasni", value: "port_of_pasni_pasni" },
  { label: "Port of Gwadar, Gwadar", value: "port_of_gwadar_gwadar" },
  { label: "Port of Keti Bandar, Keti Bandar", value: "port_of_keti_bandar" },
  { label: "Port of Qasim, Qasim", value: "port_of_qasim_qasim" },
  { label: "Port of Mukran, Mukran", value: "port_of_mukran_mukran" },
  { label: "Port of Ormara, Ormara", value: "port_of_ormara_ormara" },
  { label: "Port of Manora, Manora", value: "port_of_manora_manora" },
  { label: "Gaddani Port, Gaddani", value: "gaddani_port_gaddani" },
  { label: "Coastal Highway, Gaddani", value: "coastal_highway_gaddani" },
];

export const stepItems = [
  { title: "Load Info" },
  { title: "Goods Details" },
  { title: "Attachments" },
  { title: "Payment Info" },
  { title: "Snapshot" },
];

export const defaultNotes =
  "Ensure all quotations are approved by authorized personnel before sharing with transporters.";

let tempIdCounter = 1;
const createTempId = () => Date.now() + tempIdCounter++;

export const createGoods = () => ({
  id: createTempId(),
  goodType: "",
  quantity: "",
  handlingUnit: "",
  hsCode: "",
  weight: "",
  unit: "",
  price: "",
});

export const defaultValues = () => ({
  loadId: "",
  customer: "",
  transporterName: "",
  vehicleType: "",
  loadingPoint: "",
  offloadingPoint: "",
  departureDateTime: "",
  deliveryDateTime: "",
  goods: [createGoods()],
  isInsured: false,
  insurance: {
    company: "",
    policyNumber: "",
    coverageAmount: "",
    goodsPrice: "",
    contactNumber: "",
    email: "",
  },
  attachments: [],
  payment: {
    buyingPrice: "",
    sellingPrice: "",
    billingFrequency: "",
    paymentMode: "",
    paymentTerm: "",
    bank: "",
    accountTitle: "",
    iban: "",
  },
  notes: defaultNotes,
  shareWith: {
    whatsapp: false,
    email: false,
  },
});

const goodsShape = Yup.object().shape({
  goodType: Yup.string().required("Good Type is required"),
  quantity: Yup.string().trim().required("Quantity is required"),
  handlingUnit: Yup.string().required("Handling Unit is required"),
  hsCode: Yup.string().trim().required("Goods HS Code is required"),
  weight: Yup.string().trim().required("Weight is required"),
  unit: Yup.string().required("Unit is required"),
});

export const stepSchemas = [
  Yup.object().shape({
    customer: Yup.string().required("Customer is required"),
    vehicleType: Yup.string().required("Vehicle Type is required"),
    loadingPoint: Yup.string().required("Loading Point is required"),
    offloadingPoint: Yup.string().required("Offloading Point is required"),
    departureDateTime: Yup.string().required("Departure Date & Time is required"),
    deliveryDateTime: Yup.string().required("Delivery Date & Time is required"),
  }),
  Yup.object().shape({
    goods: Yup.array()
      .of(goodsShape)
      .min(1, "At least one goods row is required"),
    isInsured: Yup.boolean(),
    insurance: Yup.object().when("isInsured", {
      is: true,
      then: () =>
        Yup.object().shape({
          company: Yup.string().required("Insurance Company is required"),
          policyNumber: Yup.string()
            .trim()
            .required("Policy / Reference Number is required"),
          coverageAmount: Yup.string()
            .trim()
            .required("Coverage Amount is required"),
          goodsPrice: Yup.string().trim().required("Goods Price is required"),
          contactNumber: buildPhoneNumberSchema("Contact Number is required"),
          email: Yup.string()
            .trim()
            .required("Email Address is required")
            .email("Invalid email address"),
        }),
      otherwise: () => Yup.object(),
    }),
  }),
  Yup.object().shape({
    attachments: Yup.array(),
  }),
  Yup.object().shape({
    payment: Yup.object().shape({
      buyingPrice: Yup.string().trim().required("Buying Price is required"),
      sellingPrice: Yup.string().trim().required("Selling Price is required"),
      billingFrequency: Yup.string().required("Billing Frequency is required"),
      paymentMode: Yup.string().required("Payment Mode is required"),
      paymentTerm: Yup.string().required("Payment Term is required"),
      bank: Yup.string().when("paymentMode", {
        is: "bank_transfer",
        then: (schema) => schema.required("Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      accountTitle: Yup.string().when("paymentMode", {
        is: "bank_transfer",
        then: (schema) => schema.required("Account Title is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      iban: Yup.string().when("paymentMode", {
        is: "bank_transfer",
        then: (schema) => schema.required("IBAN / Account Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
  Yup.object().shape({}),
];

export const staticLoadBookings = [
  {
    id: "LDB-1001",
    customerName: "FuelOne Logistics Pvt. Ltd.",
    transporterName: "",
    loadingPoint: "Port Qasim Bin Qasim Road, Karachi",
    offloadingPoint: "Port Qasim, Bin Qasim",
    expectedDeliveryDate: "2025-10-18",
    status: "inactive",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
    formData: {
      customer: "fuelone logistics pvt. ltd.",
      vehicleType: "4_wheeler_mazda",
      loadingPoint: "port_qasim_bin_qasim",
      offloadingPoint: "karachi_port_karachi",
      departureDateTime: "2025-10-16T22:00",
      deliveryDateTime: "2025-10-18T10:00",
      notes:
        "Driver must carry original documents; offloading team will verify consignment ID.",
      goods: [
        {
          id: 1,
          goodType: "fmcgs",
          quantity: "25",
          handlingUnit: "box",
          hsCode: "123456",
          weight: "36",
          unit: "kg",
          price: "150000",
        },
      ],
      payment: {
        buyingPrice: "120000",
        sellingPrice: "150000",
        paymentMode: "bank_transfer",
        paymentTerm: "net_30",
      },
      isInsured: false,
      insurance: {
        company: "",
        policyNumber: "",
        coverageAmount: "",
        goodsPrice: "",
        contactNumber: "",
        email: "",
      },
      attachments: [],
      shareWith: { whatsapp: false, email: false },
      transporterName: "",
    },
  },
  {
    id: "LDB-1002",
    customerName: "AutoCare Solutions",
    transporterName: "IronWheel Transport",
    loadingPoint: "Karachi Port, Karachi",
    offloadingPoint: "Karachi Port, Karachi",
    expectedDeliveryDate: "2025-10-18",
    status: "active",
    attachmentsCount: 2,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1003",
    customerName: "BuildRight Hardware Mart",
    transporterName: "",
    loadingPoint: "Gaddani Port, Gaddani",
    offloadingPoint: "Port Qasim, Gaddani",
    expectedDeliveryDate: "2025-09-30",
    status: "inactive",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1004",
    customerName: "PakRoad Carriers",
    transporterName: "Fast Freight",
    loadingPoint: "Port of Pasni, Pasni",
    offloadingPoint: "Port of Pasni, Pasni",
    expectedDeliveryDate: "2025-09-30",
    status: "active",
    attachmentsCount: 1,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1005",
    customerName: "FreshNest Foods",
    transporterName: "Transit Co.",
    loadingPoint: "Port of Gwadar, Gwadar",
    offloadingPoint: "Port of Gwadar, Gwadar",
    expectedDeliveryDate: "2025-09-30",
    status: "in_transit",
    attachmentsCount: 1,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1006",
    customerName: "Nexon Builders & Co.",
    transporterName: "Track Logistics",
    loadingPoint: "Port of Keti Bandar, Keti Bandar",
    offloadingPoint: "Port of Keti Bandar",
    expectedDeliveryDate: "2025-07-15",
    status: "delayed",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1007",
    customerName: "AgroTrust Traders",
    transporterName: "Freightlines",
    loadingPoint: "Port of Qasim, Qasim",
    offloadingPoint: "Port of Qasim, Qasim",
    expectedDeliveryDate: "2025-07-15",
    status: "completed",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1008",
    customerName: "MediCore Distributors",
    transporterName: "CargoCrafter",
    loadingPoint: "Port of Mukran, Mukran",
    offloadingPoint: "Port of Mukran, Mukran",
    expectedDeliveryDate: "2025-06-09",
    status: "completed",
    attachmentsCount: 2,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1009",
    customerName: "SmartCart Retailers",
    transporterName: "Go NorthStar",
    loadingPoint: "Port of Ormara, Ormara",
    offloadingPoint: "Port of Ormara, Ormara",
    expectedDeliveryDate: "2025-02-08",
    status: "completed",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
  {
    id: "LDB-1010",
    customerName: "Metro Miles Traders",
    transporterName: "Metro Mile Movers",
    loadingPoint: "Port of Manora, Manora",
    offloadingPoint: "Port of Manora, Manora",
    expectedDeliveryDate: "2025-02-08",
    status: "delayed",
    attachmentsCount: 0,
    updatedAt: "2025-09-28T10:00:00.000Z",
  },
];
